import { Button, Card, CardContent, Input, Label } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Mail, Smartphone } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { acceptInvitation } from "../api/auth.js";
import { getCurrentUser } from "../api/client.js";
import { ApiError, displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { AuthScreen, type AuthMode } from "./auth-screen.js";
import {
  clearPendingInvitationToken,
  invitationTokenFromInput,
  pendingInvitationToken,
  rememberInvitationToken,
} from "./invitations.js";

function InvitationFrame({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-dvh bg-canvas px-5 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <Brand />
        <div className="mt-10">{children}</div>
      </div>
    </main>
  );
}

function InvitationEntry() {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const token = invitationTokenFromInput(input);
    if (!token) {
      setError("Paste the complete invitation link sent by your manager.");
      return;
    }
    void navigate({
      search: { token },
      to: "/invitations/accept",
    });
  }

  return (
    <InvitationFrame>
      <Card>
        <CardContent className="p-6 sm:p-8">
          <span className="grid size-11 place-items-center rounded-2xl bg-emerald-100 text-emerald-800">
            <Mail className="size-5" aria-hidden="true" />
          </span>
          <h1 className="mt-5 text-3xl font-black tracking-[-0.04em]">
            Join your workplace
          </h1>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            Your manager must invite you from the SafePlate Team page. Open the
            secure link in that email, or paste the complete link below.
          </p>
          <form className="mt-6 space-y-3" onSubmit={submit}>
            <Label htmlFor="invitation-link">Invitation link</Label>
            <Input
              id="invitation-link"
              onChange={(event) => setInput(event.target.value)}
              placeholder="https://…/invitations/accept?token=…"
              required
              value={input}
            />
            {error ? (
              <p className="text-sm font-semibold text-red-800" role="alert">
                {error}
              </p>
            ) : null}
            <Button className="w-full" type="submit">
              Continue with invitation
            </Button>
          </form>
          <p className="mt-5 text-xs leading-5 text-stone-500">
            There is no reusable workplace code. Invitations are single-use,
            expire after seven days, and assign only the role and locations
            chosen by your manager.
          </p>
        </CardContent>
      </Card>
    </InvitationFrame>
  );
}

export function InvitationAcceptScreen({ token }: { token?: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const resolvedToken =
    invitationTokenFromInput(token ?? "") ?? pendingInvitationToken();
  const [mode, setMode] = useState<AuthMode>("sign-up");
  const acceptanceStarted = useRef(false);

  const me = useQuery({
    enabled: Boolean(resolvedToken),
    queryFn: getCurrentUser,
    queryKey: ["invitation", "me"],
    retry: false,
  });
  const acceptance = useMutation({
    mutationFn: () => acceptInvitation(resolvedToken!),
    onSuccess: async () => {
      clearPendingInvitationToken();
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      await queryClient.invalidateQueries({ queryKey: ["invitation", "me"] });
      await navigate({ replace: true, to: "/" });
    },
  });
  const accept = acceptance.mutate;

  useEffect(() => {
    if (resolvedToken) rememberInvitationToken(resolvedToken);
  }, [resolvedToken]);

  useEffect(() => {
    if (!me.data || !resolvedToken || acceptanceStarted.current) return;
    acceptanceStarted.current = true;
    accept();
  }, [accept, me.data, resolvedToken]);

  if (!resolvedToken) return <InvitationEntry />;

  if (me.isPending) {
    return (
      <InvitationFrame>
        <LoadingState label="Opening your invitation" />
      </InvitationFrame>
    );
  }

  if (me.error instanceof ApiError && me.error.status === 401) {
    return (
      <AuthScreen
        beforeSocialRedirect={() => rememberInvitationToken(resolvedToken)}
        context="invitation"
        key={mode}
        mode={mode}
        onAuthenticated={async () => {
          acceptanceStarted.current = false;
          await queryClient.invalidateQueries({
            queryKey: ["invitation", "me"],
          });
        }}
        onSwitchMode={() =>
          setMode((current) => (current === "sign-in" ? "sign-up" : "sign-in"))
        }
      />
    );
  }

  if (me.error) {
    return (
      <InvitationFrame>
        <ErrorState
          message={displayError(me.error)}
          onAction={() => void me.refetch()}
          title="Your invitation didn’t open"
        />
      </InvitationFrame>
    );
  }

  if (acceptance.error) {
    return (
      <InvitationFrame>
        <ErrorState
          actionLabel="Try invitation again"
          message={displayError(acceptance.error)}
          onAction={() => {
            acceptanceStarted.current = false;
            acceptance.mutate();
          }}
          title="We couldn’t join the workplace"
        />
      </InvitationFrame>
    );
  }

  return (
    <InvitationFrame>
      <Card>
        <CardContent className="grid min-h-64 place-items-center p-8 text-center">
          <div>
            {acceptance.isSuccess ? (
              <CheckCircle2 className="mx-auto size-8 text-emerald-700" />
            ) : (
              <Smartphone className="mx-auto size-8 text-emerald-700" />
            )}
            <h1 className="mt-4 text-xl font-black">Joining your workplace…</h1>
            <p className="mt-2 text-sm text-stone-500">
              SafePlate is linking your account to the invited role and
              locations.
            </p>
            <a
              className="mt-5 inline-flex text-sm font-bold text-emerald-800 hover:text-emerald-950"
              href={`safeplate://invite?token=${encodeURIComponent(resolvedToken)}`}
            >
              Open this invitation in the SafePlate app
            </a>
          </div>
        </CardContent>
      </Card>
    </InvitationFrame>
  );
}
